import _ from "lodash";
import React, { useState, useRef, useEffect, useMemo } from "react";
import useVirtualizedUserDetails from "MyCalendar/hooks/useVirtualizedUserDetails";

import {
  SearchInput,
  EmptyState,
  ChecklistDropdown,
  FilterLabelButton,
  Dropdown,
  IconDisclosure,
  FilterMenu,
  DropdownMenu,
} from "@toddle-edu/ds-web";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";
import { FilterOutlined } from "@toddle-edu/ds-icons";

import { useDebouncedCallback, useHandleScroll } from "Utils/CustomHooks";

import { useI18n } from "Hooks";
import Loading from "UIComponents/Loading";

import { ADD_MEMBERS_COHORTS } from "MessagingV2/modules/Utils";

import UserList from "../UserList";
import CohortList from "../CohortList";
import classes from "./UsersAndCohorts.scss";
import { useMetadata } from "../MetadataProvider";
import { getLabelValueForMultiFilterLabelButton } from "Utils";
import classNames from "classnames";

const EmptySearch = ({ searchText }) => {
  const { t } = useI18n(["common"]);
  const { emptyStateSubtitle } = useMetadata();

  return (
    <div className={classes.emptyStateContainer}>
      <div className={classes.emptyState}>
        <EmptyState
          dsVersion="2.0"
          illustration={NoSearchInFilterIllustration}
          title={searchText ? t("common:no_result_found") : ""}
          subtitle={
            searchText
              ? t("common:no_results_with_label", {
                  label: searchText,
                })
              : t(emptyStateSubtitle)
          }
        />
      </div>
    </div>
  );
};

const getScrollParams = ({
  membersParams,
  courseUserParams,
  // gradeUserParams,
  //   yearGroupUserParams,
}) => {
  const pickParams = ["networkStatus", "loadMoreFeed", "hasNextPage"];

  if (_.get(courseUserParams, "hasNextPage"))
    return _.pick(courseUserParams, pickParams);

  if (_.get(membersParams, "hasNextPage"))
    return _.pick(membersParams, pickParams);

  return {
    networkStatus: 1,
    loadMoreFeed: _.noop,
    hasNextPage: false,
  };
};

const VISIBILITY_LEVEL = {
  EMPTY: 0,
  YEAR_GROUP: 1,
  GRADE: 2,
  COURSE: 3,
  MEMBERS: 4,
  ALL: 5,
};

const getCohortVisibility = ({
  searchText,
  courseUserParams,
  gradeUserParams,
  yearGroupUserParams,
  membersParams,
  showSearchAndFilter = true,
  showEmptyTextQuery = false,
}) => {
  if (
    (_.isEmpty(_.trim(searchText)) &&
      showSearchAndFilter &&
      !showEmptyTextQuery) ||
    (_.isEmpty(courseUserParams.cohorts) &&
      _.isEmpty(yearGroupUserParams.cohorts) &&
      _.isEmpty(gradeUserParams.cohorts) &&
      _.isEmpty(membersParams.userNodeList))
  ) {
    return VISIBILITY_LEVEL.EMPTY;
  }

  if (_.get(yearGroupUserParams, "hasNextPage", false)) {
    return VISIBILITY_LEVEL.YEAR_GROUP;
  }

  if (_.get(gradeUserParams, "hasNextPage", false)) {
    return VISIBILITY_LEVEL.GRADE;
  }

  if (_.get(courseUserParams, "hasNextPage", false)) {
    return VISIBILITY_LEVEL.COURSE;
  }

  if (_.get(membersParams, "hasNextPage", false)) {
    return VISIBILITY_LEVEL.MEMBERS;
  }

  return VISIBILITY_LEVEL.ALL;
};

export const UsersAndCohortsList = ({
  searchText = "",
  courseUserParams = {},
  gradeUserParams = {},
  yearGroupUserParams = {},
  membersParams = {},
  cohortState = {},
  selectedUsersState = {},
  onAddUsers,
  onRemoveUsers,
  onAddCohort,
  onRemoveCohort,
  prevUsers = [],
  emptyStateSubtitle,
  showSearchAndFilter,
  typeFilter,
  testId,
  showCohortHeader = true,
  showEmptyTextQuery = false,
  isRevampEnabled = false,
  setIsResultEmpty = _.noop,
  showStudentId,
  enableLazyHydration = false,
  showSchoolName = false,
  staffCurriculumProgramLabel,
}) => {
  const flatUsers = useMemo(
    () =>
      _.map(_.get(membersParams, "userNodeList", []), e => _.get(e, "node", e)),
    [membersParams]
  );
  const {
    onRangeChanged: onMembersRangeChanged,
    getUserDetails: getMemberDetails,
  } = useVirtualizedUserDetails({
    users: flatUsers,
    enabled: enableLazyHydration,
  });
  const { t } = useI18n(["common"]);

  const visibilityLevel = getCohortVisibility({
    searchText,
    yearGroupUserParams,
    courseUserParams,
    gradeUserParams,
    membersParams,
    showSearchAndFilter,
    showEmptyTextQuery,
  });

  // this is used to hide the empty state of selected users when no search result is found
  useEffect(() => {
    const isEmpty = _.eq(visibilityLevel, VISIBILITY_LEVEL.EMPTY);
    setIsResultEmpty(isEmpty);
  }, [visibilityLevel, setIsResultEmpty]);

  useEffect(() => {
    if (!isRevampEnabled || visibilityLevel === VISIBILITY_LEVEL.EMPTY) return;
    containerRef.current.scrollTo({
      top: 0,
    });
  }, [typeFilter, searchText, isRevampEnabled, visibilityLevel]);

  const componentTestId = testId ? `${testId}-usersAndCohortsList` : undefined;
  const { networkStatus, loadMoreFeed, hasNextPage } = getScrollParams({
    yearGroupUserParams,
    courseUserParams,
    gradeUserParams,
    membersParams,
  });

  const isFetchingMore = _.includes([3], networkStatus);

  const containerRef = useRef();

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef: () => containerRef.current,
  });

  if (_.eq(visibilityLevel, VISIBILITY_LEVEL.EMPTY)) {
    return <EmptySearch searchText={searchText} />;
  }

  const membersHeaderTitle = _.get(membersParams, "headerTitle")
    ? t(_.get(membersParams, "headerTitle"))
    : t("common:members");

  return (
    <div className={classes.usersAndCohortsList} ref={containerRef}>
      {_.lte(VISIBILITY_LEVEL.YEAR_GROUP, visibilityLevel) ? (
        <CohortList
          headerTitle={t("common:year_group")}
          onAddCohort={onAddCohort}
          onRemoveCohort={onRemoveCohort}
          selectedState={cohortState[ADD_MEMBERS_COHORTS.YEAR_GROUP]}
          type={ADD_MEMBERS_COHORTS.YEAR_GROUP}
          cohorts={yearGroupUserParams.cohorts}
          testId={componentTestId}
          showCohortHeader={showCohortHeader}
          isRevampEnabled={isRevampEnabled}
        />
      ) : null}

      {_.lte(VISIBILITY_LEVEL.GRADE, visibilityLevel) ? (
        <CohortList
          headerTitle={t("common:grades")}
          onAddCohort={onAddCohort}
          onRemoveCohort={onRemoveCohort}
          selectedState={cohortState[ADD_MEMBERS_COHORTS.GRADE]}
          type={ADD_MEMBERS_COHORTS.GRADE}
          cohorts={gradeUserParams.cohorts}
          testId={componentTestId}
          showCohortHeader={showCohortHeader}
          isRevampEnabled={isRevampEnabled}
        />
      ) : null}
      {_.lte(VISIBILITY_LEVEL.COURSE, visibilityLevel) ? (
        <CohortList
          headerTitle={t("common:class_plural")}
          onAddCohort={onAddCohort}
          onRemoveCohort={onRemoveCohort}
          selectedState={cohortState[ADD_MEMBERS_COHORTS.CLASSES]}
          type={ADD_MEMBERS_COHORTS.CLASSES}
          cohorts={courseUserParams.cohorts}
          testId={componentTestId}
          showCohortHeader={showCohortHeader}
          isRevampEnabled={isRevampEnabled}
        />
      ) : null}

      {_.lte(VISIBILITY_LEVEL.MEMBERS, visibilityLevel) ? (
        <UserList
          headerTitle={membersHeaderTitle}
          membersParams={membersParams}
          onAddUsers={onAddUsers}
          onRemoveUsers={onRemoveUsers}
          selectedUsersState={selectedUsersState}
          prevUsers={prevUsers}
          testId={componentTestId}
          showCohortHeader={showCohortHeader}
          isRevampEnabled={isRevampEnabled}
          showStudentId={showStudentId}
          showSchoolName={showSchoolName}
          staffCurriculumProgramLabel={staffCurriculumProgramLabel}
          onRangeChanged={
            enableLazyHydration ? onMembersRangeChanged : undefined
          }
          getUserDetails={enableLazyHydration ? getMemberDetails : undefined}
        />
      ) : null}
      {isFetchingMore ? <Loading /> : null}
    </div>
  );
};

// DefaultQueryWrapper is a function that returns null to prevent error
const defaultQueryWrapper = () => null;

const getIsGrouped = filters => _.isArray(_.first(filters)?.options);

const markDisabled = (item, disabledOptions) => {
  const isDisabled =
    _.includes(disabledOptions, item.key) || item.disabled || item.isDisabled;
  return { ...item, disabled: isDisabled, isDisabled };
};

const Filter = props => {
  const {
    isChecklistFilter,
    typeFilterOptions,
    typeFilter,
    setTypeFilter,
    getDisabledFilterOptions,
  } = props;
  const { filterLabelText, filterLabelDefaultValue = [] } = useMetadata();
  const { t } = useI18n();
  const disabledOptions = useMemo(() => getDisabledFilterOptions(typeFilter), [
    typeFilter,
    getDisabledFilterOptions,
  ]);
  const isGrouped = getIsGrouped(typeFilterOptions);

  // de-select any disabled options from the typeFilter state
  useEffect(() => {
    if (_.isEmpty(disabledOptions) || !isGrouped) return;

    setTypeFilter(prev => {
      const shouldUpdate = _.some(disabledOptions, key => key in prev);
      return shouldUpdate ? _.omit(prev, disabledOptions) : prev;
    });
  }, [disabledOptions, isGrouped, setTypeFilter]);

  const processedTypeFilterOptions = useMemo(() => {
    return _.map(typeFilterOptions, option =>
      markDisabled(option, disabledOptions)
    );
  }, [typeFilterOptions, disabledOptions]);

  const handleFilterReset = () => {
    setTypeFilter(filterLabelDefaultValue);
  };

  return isChecklistFilter ? (
    <ChecklistDropdown
      options={processedTypeFilterOptions}
      showAllOption
      value={typeFilter}
      onChange={setTypeFilter}
      showSelectedOptionsOnTop={false}
      dsVersion="2.0"
    >
      <FilterLabelButton
        dsVersion="2.0"
        size="medium"
        variant="neutral"
        className={classes.groupByButton}
        type="outlined"
        label={t(filterLabelText.label)}
        labelValue={getLabelValueForMultiFilterLabelButton({
          selectedOptions: typeFilter,
          options: processedTypeFilterOptions,
          t,
          noneText: t(filterLabelText.noneText),
        })}
      />
    </ChecklistDropdown>
  ) : isGrouped ? (
    <FilterMenu
      options={processedTypeFilterOptions}
      onResetSelection={handleFilterReset}
      onChange={setTypeFilter}
      selectedItemKeys={typeFilter}
      showSelectedOptionsOnTop={false}
    >
      <IconDisclosure
        dsVersion="2.0"
        icon={<FilterOutlined />}
        type="outlined"
        variant="neutral"
      />
    </FilterMenu>
  ) : (
    <Dropdown
      dsVersion="2.0"
      overlay={
        <DropdownMenu
          dsVersion="2.0"
          options={processedTypeFilterOptions}
          value={typeFilter}
          onClick={({ key }) => setTypeFilter(key)}
          showSelection={true}
          selectionType="radio"
        />
      }
    >
      <FilterLabelButton
        dsVersion="2.0"
        variant="neutral"
        type="outlined"
        className={classes.groupByButton}
        label={t(filterLabelText.label)}
        labelValue={
          _.find(processedTypeFilterOptions, { key: typeFilter })?.label
        }
      />
    </Dropdown>
  );
};

const UsersAndCohorts = props => {
  // The above UsersAndCohortsList component is imported and rendered
  // directly in the renderQueryWrapper's returned Component
  const {
    renderQueryWrapper = defaultQueryWrapper,
    showTypeFilter,
    typeFilterOptions,
    dsVersion,
    searchPlaceholder = "common:search_for_user_class_grade_placeholder",
    testId,
    showSearchAndFilter = true,
    showEmptyTextQuery = false,
    emptyTextQueryCond = () => {},
    autoFocusSearchInput = true,
    isChecklistFilter = true,
    isRevampEnabled = false,
    showStudentId = false,
    getDisabledFilterOptions = () => [],
    leftCustomHeader = null,
    enableLazyHydration = false,
    ...restProps
  } = props;
  const { t } = useI18n(["common", "integration"]);

  const { filterLabelDefaultValue = [] } = useMetadata();

  const [typeFilter, setTypeFilter] = useState(filterLabelDefaultValue);

  const componentTestId = testId ? `${testId}-userAndCohorts` : undefined;
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [debouncedHandleSearchTextChange] = useDebouncedCallback(
    setDebouncedSearchText,
    300
  );

  const isEmptySearchText = _.isEmpty(_.trim(debouncedSearchText));

  const shouldShowEmptySearchQueryData =
    showEmptyTextQuery && emptyTextQueryCond?.({ ...props, typeFilter });

  const searchContainerClasses = classNames({
    [classes.searchContainer]: !isRevampEnabled,
    [classes.searchContainerRevamp]: isRevampEnabled,
  });

  return (
    <div className={classes.rootContainer}>
      {showSearchAndFilter ? (
        <div className={searchContainerClasses}>
          <SearchInput
            {...(_.isEqual(dsVersion, "2.0")
              ? { dsVersion: "2.0" }
              : { size: "medium" })}
            onChange={debouncedHandleSearchTextChange}
            placeholder={t(searchPlaceholder)}
            testId={componentTestId}
            autoFocus={autoFocusSearchInput}
          />

          {leftCustomHeader}

          {showTypeFilter ? (
            <Filter
              isChecklistFilter={isChecklistFilter}
              typeFilterOptions={typeFilterOptions}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              getDisabledFilterOptions={getDisabledFilterOptions}
            />
          ) : null}
        </div>
      ) : null}
      <div className={classes.cohortsAndUsersListContainer}>
        {showSearchAndFilter &&
        isEmptySearchText &&
        !shouldShowEmptySearchQueryData ? (
          <EmptySearch />
        ) : (
          renderQueryWrapper({
            ...restProps,
            searchText: debouncedSearchText,
            showSearchAndFilter,
            typeFilter,
            isLoading: false,
            isData: true,
            showEmptyTextQuery: shouldShowEmptySearchQueryData,
            isRevampEnabled,
            showStudentId,
            enableLazyHydration,
          })
        )}
      </div>
    </div>
  );
};

export default UsersAndCohorts;
